import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Radio, message, Descriptions, Spin, Modal } from 'antd';
import { ArrowLeftOutlined, WechatOutlined, AlipayOutlined } from '@ant-design/icons';
import { getProductDetail, createPayment } from '../api/product';
import { formatPrice } from '../utils/format';
import { localizeProduct } from '../utils/localizeProduct';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS } from '../constants';
import { getToken } from '../utils/auth';
import { useI18n } from '../i18n/I18nContext';
import alipayQR from '../assets/images/wxpay.jpg';
import wechatQR from '../assets/images/alpay.jpg';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [product, setProduct] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.ALIPAY);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    fetchProductDetail();
  }, [id]);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      const response = await getProductDetail(id);
      if (response.success) {
        setProduct(response.data);
      }
    } catch (error) {
      console.error('获取商品详情失败:', error);
      message.error(t('product.loadError'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    const token = getToken();
    if (!token) {
      message.warning(t('product.loginToPay'));
      sessionStorage.setItem('returnToProduct', id);
      navigate('/login');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await createPayment({
        productId: id,
        paymentMethod: PAYMENT_METHOD_LABELS[paymentMethod],
        amount: product.price,
      });
      if (response.success) {
        setPaymentData(response);
        setQrModalVisible(true);
      }
    } catch (error) {
      console.error('支付失败:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleModalClose = () => {
    setQrModalVisible(false);
  };

  const paymentDisplayName =
    paymentMethod === PAYMENT_METHODS.ALIPAY ? t('payment.alipay') : t('payment.wechat');

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('product.loading')} />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const display = localizeProduct(product, locale);
  const noImageUrl = `https://via.placeholder.com/500?text=${encodeURIComponent(t('home.noImage'))}`;

  return (
    <div className="detail-container">
      <div className="detail-header-bar">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-button"
        >
          {t('product.backHome')}
        </Button>
      </div>

      <Card className="detail-card">
        <div className="product-detail-wrapper">
          <div className="product-image-section">
            <img
              src={product.image}
              alt={display.name}
              className="detail-image"
              onError={(e) => {
                e.target.src = noImageUrl;
              }}
            />
          </div>

          <div className="product-info-section">
            <h1 className="product-title">{display.name}</h1>
            <p className="product-price">{formatPrice(product.price, locale)}</p>
            <p className="product-description">{display.description}</p>

            <Descriptions title={t('product.specs')} column={1} className="product-specs">
              {display.details.split('\n').map((line, index) => {
                const [label, value] = line.split(': ');
                return value ? (
                  <Descriptions.Item label={label} key={index}>
                    {value}
                  </Descriptions.Item>
                ) : null;
              })}
            </Descriptions>

            <div className="payment-section">
              <h3>{t('product.choosePayment')}</h3>
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="payment-options"
              >
                <Radio.Button value={PAYMENT_METHODS.ALIPAY} className="payment-button">
                  <AlipayOutlined /> {t('payment.alipay')}
                </Radio.Button>
                <Radio.Button value={PAYMENT_METHODS.WECHAT} className="payment-button">
                  <WechatOutlined /> {t('payment.wechat')}
                </Radio.Button>
              </Radio.Group>
              <Button
                type="primary"
                size="large"
                onClick={handlePayment}
                loading={paymentLoading}
                className="pay-button"
              >
                {t('product.payNow')}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Modal
        title={null}
        open={qrModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={400}
        centered
        className="payment-qr-modal"
      >
        <div className="qr-code-container">
          <div className="payment-method-header">
            {paymentMethod === PAYMENT_METHODS.ALIPAY ? (
              <>
                <AlipayOutlined style={{ fontSize: 32, color: '#1677ff' }} />
                <h3>{t('product.alipayTitle')}</h3>
              </>
            ) : (
              <>
                <WechatOutlined style={{ fontSize: 32, color: '#07c160' }} />
                <h3>{t('product.wechatTitle')}</h3>
              </>
            )}
          </div>

          <div className="qr-code-wrapper">
            <img
              src={paymentMethod === PAYMENT_METHODS.ALIPAY ? alipayQR : wechatQR}
              alt={`${paymentDisplayName} ${t('product.qrAlt')}`}
              className="qr-code-image"
            />
          </div>

          <div className="payment-info">
            <p className="payment-amount">
              {t('product.amount')}
              <span>{formatPrice(product.price, locale)}</span>
            </p>
            <p className="payment-tip">{t('product.scanTip', { method: paymentDisplayName })}</p>
            <p className="order-number">
              {t('product.orderNo')}
              {paymentData?.orderId}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ProductDetail;
